import {Injectable, inject} from '@angular/core'
import {NavController} from '@ionic/angular/standalone'
import {Actions, createEffect, ofType} from '@ngrx/effects'
import {Store} from '@ngrx/store'
import {forkJoin, of} from 'rxjs'
import {map, mergeMap, switchMap, withLatestFrom} from 'rxjs/operators'
import {ToastNotificationService} from '@common/components/toast-notification/toast-notification.service'
import {INFOUSER, Operations} from '@common/global-constants'
import {catchSwitchMapError} from '@common/utils/owner-rxjs-operators.util'
import {Action} from '@models/action'
import {Activity} from '@models/activity'
import {ActionResponseOperation} from '@models/enums/action-response.enum'
import {Group} from '@models/group'
import {Outlet} from '@models/outlet'
import {ActivityEffectAction} from '@models/types/action-response.type'
import {UVEuser} from '@models/uve-user'
import {updateOutletOnList} from '@pages/home/store/home.actions'
import {setScheduleOutlet} from '@pages/schedule/store/schedule.actions'
import {ActionService} from '@service/actions/action.service'
import {ActivityService} from '@service/activity.service'
import {ApiService} from '@service/api.service'
import {ConfigurationService} from '@service/configuration/configuration.service'
import {CustomizeService} from '@service/customize.service'
import {DeliveryDateService} from '@service/deliveryDate.service'
import {GroupService} from '@service/group.service'
import {InfoService} from '@service/info/info.service'
import {LoggingService} from '@service/logging.service'
import {OutletService} from '@service/outlet/outlet.service'
import {TranslationServicex} from '@service/translationx.service'
import {OutletDetailsActionsTypes, displayOutletActionsResponse, updateOutletDetail} from '../outlet-details.actions'
import {
  ActivitiesActionsTypes,
  clearActivityResponseResult,
  deleteActivityTaskAnswersResponse,
  endActivityResponse,
  getAppointmentFormValuesResponse,
  getOutletActivitiesResponse,
  saveActivityInfoRequest,
  saveActivityResponseResult,
} from './activities.actions'
import {ActivityObjective, ActivityReason, ActivityType} from './activities.reducer'
import {getAllowedActivitiesSelector, getLastActivitySelector} from './activities.selectors'

@Injectable()
export class ActivitiesEffects {
  private readonly actions$ = inject(Actions)
  private readonly store = inject(Store)
  private readonly activityService = inject(ActivityService)
  private readonly apiService = inject(ApiService)
  private readonly groupService = inject(GroupService)
  private readonly infoService = inject(InfoService)
  private readonly actionService = inject(ActionService)
  private readonly customize = inject(CustomizeService)
  private readonly deliveryServices = inject(DeliveryDateService)
  private readonly configService = inject(ConfigurationService)
  private readonly translationServicex = inject(TranslationServicex)
  private readonly outletService = inject(OutletService)
  private readonly navController = inject(NavController)
  private readonly toastNotificationService = inject(ToastNotificationService)
  private readonly loggingService = inject(LoggingService)

  outletDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OutletDetailsActionsTypes.DETAILS_REQUEST),
      switchMap(({outlet, id}: {outlet: Outlet; id: string}) =>
        forkJoin({
          outlet: this.outletService.getOutlet(id),
          actions: this.actionService.getActionsByOutlet(id),
        })
      ),
      switchMap(({outlet, actions}: {outlet: Outlet; actions: Action[]}) => {
        return forkJoin({
          outlet: of(outlet),
          activityData: this.infoService.getActivitiesDataAll(actions),
        })
      }),
      map(
        ({
          outlet,
          activityData,
        }: {
          outlet: Outlet
          activityData: {
            types: ActivityType[]
            reasons: ActivityReason[]
            objectives: ActivityObjective[]
          }
        }) =>
          getOutletActivitiesResponse({
            outlet,
            allowedActivities: activityData.types,
            activityReasons: activityData.reasons,
            objectives: activityData.objectives,
          })
      )
    )
  )

  initActivity$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ActivitiesActionsTypes.INIT_ACTIVITY_REQUEST),
      switchMap(({id, outlet}) => this.activityService.startActivity(outlet, id)),
      mergeMap(([activity, outlet]: [Activity, Outlet]) => [
        updateOutletOnList({outlet}),
        setScheduleOutlet({outlet}),
        getOutletActivitiesResponse({
          activity,
          outlet,
        }),
        displayOutletActionsResponse({
          value: true,
        }),
        updateOutletDetail({outlet}),
      ])
    )
  )

  endActivity$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ActivitiesActionsTypes.END_ACTIVITY_REQUEST),
      switchMap(({activity, isEffective, outlet}: {activity: Activity; isEffective: boolean; outlet: Outlet}) => {
        if (!outlet.isNew) {
          return of({activity, isEffective, outlet})
        }

        const outletUpdated = {...outlet, isNew: false}

        return this.outletService.updateOutlet(outletUpdated, false).then(() => ({
          activity,
          isEffective,
          outlet: outletUpdated,
        }))
      }),
      switchMap(({activity, isEffective, outlet}) => this.activityService.endActivity(activity, isEffective, outlet)),
      switchMap(([activity, outlet]) => of(endActivityResponse({activity, outlet}))),
      catchSwitchMapError(error =>
        saveActivityResponseResult({
          operation: ActionResponseOperation.ERROR,
          data: null,
          error,
          action: ActivityEffectAction.FINISH,
        })
      )
    )
  )

  endActivityResponse$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ActivitiesActionsTypes.END_ACTIVITY_RESPONSE),
      mergeMap(({outlet, activity}: {outlet: Outlet; activity: Activity}) => {
        const outletId = outlet.id

        return [
          saveActivityInfoRequest({
            outletId,
            activityInfo: null,
          }),
          saveActivityResponseResult({
            data: {outletId: outletId.toString()},
            operation: ActionResponseOperation.SUCCESS,
            error: null,
            action: ActivityEffectAction.FINISH,
          }),
          displayOutletActionsResponse({value: false}),
          updateOutletOnList({outlet}),
          setScheduleOutlet({outlet}),
        ]
      })
    )
  )

  // addAction$ y postAddAction$ effects eliminados - ahora se usan métodos síncronos directamente
  // addActionActivitySync y updateActionActivitySync manejan toda la lógica incluyendo customizaciones y navegación

  // addActionResponse$ effect mantenido para disparar saveActivityResponseResult cuando se completa una acción
  // Esto permite que componentes que escuchan getActivityResponseResultSelector sigan funcionando
  addActionResponse$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ActivitiesActionsTypes.ADD_ACTION_RESPONSE),
      map(({activity, action}: {activity: Activity; action: Action}) => {
        const outletId = activity?.targetId?.toString()
        const actionTypeId = action?.actionType

        return saveActivityResponseResult({
          data: {outletId, actionTypeId},
          operation: ActionResponseOperation.SUCCESS,
          error: null,
          action: ActivityEffectAction.SAVE,
        })
      })
    )
  )

  saveActivityResponseResult$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ActivitiesActionsTypes.SAVE_ACTIVITY_RESPONSE_RESULT),
      switchMap(() => of(clearActivityResponseResult()))
    )
  )

  getActivity = createEffect(() =>
    this.actions$.pipe(
      ofType(ActivitiesActionsTypes.GET_ACTIVITY_REQUEST),
      withLatestFrom(this.store.select(getLastActivitySelector), this.store.select(getAllowedActivitiesSelector)),
      switchMap(([{id, outletId}, last, allowedActivities]) =>
        forkJoin({
          activity: last ? of(last) : this.activityService.getActivity(id),
          outlet: this.outletService.getOutlet(outletId),
          allowedActivities: allowedActivities ? of(allowedActivities) : this.infoService.getActivitiesTypes(),
        })
      ),
      map(
        ({
          activity,
          outlet,
          allowedActivities,
        }: {
          activity: Activity
          outlet: Outlet
          allowedActivities: {id: string; label: string}[]
        }) =>
          getOutletActivitiesResponse({
            activity,
            outlet,
            allowedActivities,
          })
      )
    )
  )

  appointmentFormValues$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ActivitiesActionsTypes.GET_APPOINTMENT_FORM_VALUES_REQUEST),
      switchMap(({outletId}) =>
        forkJoin({
          outlet: outletId ? this.outletService.getOutlet(outletId) : of(null),
          groups: outletId ? this.groupService.getGroups() : of(null),
          user: outletId ? this.apiService.getItemLocalInfo<UVEuser>(INFOUSER) : of(null),
          actions: outletId ? this.actionService.getActionsByOutlet(outletId) : of(null),
        })
      ),
      switchMap(
        ({outlet, groups, user, actions}: {outlet: Outlet; groups: Group[]; user: UVEuser; actions: Action[]}) =>
          forkJoin({
            objectives: this.infoService.getActivitiesObjectives(actions),
            types: this.infoService.getActivitiesTypes(actions),
            outlet: of(outlet),
            groups: of(groups),
            user: of(user),
          })
      ),
      mergeMap(
        ({
          objectives,
          types,
          outlet,
          groups,
          user,
        }: {
          objectives: ActivityObjective[]
          types: ActivityType[]
          outlet: Outlet
          groups: Group[]
          user: UVEuser
        }) =>
          forkJoin({
            objectives: of(objectives),
            types: outlet ? of(this.activityService.getAllowedActivityTypes(types, groups, user, outlet)) : of(types),
          })
      ),
      map(({objectives, types}: {objectives: ActivityObjective[]; types: ActivityType[]}) =>
        getAppointmentFormValuesResponse({
          objectives,
          types,
        })
      )
    )
  )

  deleteTaskAnswers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ActivitiesActionsTypes.DELETE_ACTIVITY_TASK_ANSWERS_REQUEST),
      switchMap(({activity, actionId}: {activity: Activity; actionId: number}) => {
        const activitySaveLocal = {
          ...this.deleteActivityTaskAnswers(activity, actionId),
        }
        return forkJoin({
          result: this.activityService.saveActivity(Operations.UPDATE, activitySaveLocal),
        })
      }),
      mergeMap(({result}: {result: Activity}) => {
        const actions = []

        if (result) {
          this.toastNotificationService.success(this.translationServicex.instant('removed_successfully'))
          actions.push(deleteActivityTaskAnswersResponse({activity: result}))
        } else {
          this.toastNotificationService.error(this.translationServicex.instant('delete_action_error'))
        }

        return actions
      })
    )
  )

  deleteActivityTaskAnswersResponse$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ActivitiesActionsTypes.DELETE_ACTIVITY_TASK_ANSWERS_RESPONSE),
      map(({activity}: {activity: Activity}) => {
        return saveActivityResponseResult({
          data: {outletId: activity?.targetId?.toString()},
          operation: ActionResponseOperation.SUCCESS,
          error: null,
          action: ActivityEffectAction.DELETE_TASKS_ANSWERS,
        })
      })
    )
  )

  deleteTaskPhoto$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ActivitiesActionsTypes.DELETE_ACTIVITY_TASK_PHOTO_REQUEST),
      switchMap(({activity, idDocAdjunto}: {activity: Activity; idDocAdjunto: string}) => {
        const activitySaveLocal = {
          ...this.deleteActivityTaskPhoto(activity, idDocAdjunto),
        }
        return forkJoin({
          result: this.activityService.saveActivity(Operations.UPDATE, activitySaveLocal),
        })
      }),
      mergeMap(({result}: {result: Activity}) => {
        const actions = []

        if (result) {
          actions.push(deleteActivityTaskAnswersResponse({activity: result}))
        }

        return actions
      })
    )
  )

  private readonly deleteActivityTaskAnswers = (activity: Activity, actionId: number): Activity => ({
    ...activity,
    tasks: activity.tasks.filter(t => t.idAccion?.toString() !== actionId?.toString()),
  })

  private readonly deleteActivityTaskPhoto = (activity: Activity, idDocAdjunto: string) => ({
    ...activity,
    tasks: activity.tasks.filter(t => t.idDocAdjunto?.toString() !== idDocAdjunto?.toString()),
  })
}
